import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { loadPublishedDefinition } from '@/backend/loadDefinition';
import { buildRemix, canRemix } from '@/onboarding/remix';
import { useMyAppsStore } from '@/store/myAppsStore';
import { useProjectStore } from '@/store/projectStore';
import { filterApps, listCategories } from '@/utils/searchApps';
import { Button, EmptyState, IdentityChip, Screen } from '../ui';

/**
 * Ported from src/app/(main)/(tabs)/discover.tsx, filtering through the app's own
 * filterApps/listCategories so the browser and the phone agree on what matches.
 */
export default function Discover() {
  const navigate = useNavigate();
  const catalog = useMyAppsStore((s) => s.catalog);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [cloning, setCloning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void useMyAppsStore.getState().loadCatalog();
  }, []);

  const categories = useMemo(() => listCategories(catalog), [catalog]);
  const shown = useMemo(() => filterApps(catalog, query, category), [catalog, query, category]);

  /** Clone a remix-only demo into an editable copy the person owns, then open it. */
  async function remix(appId: string, name: string, description: string): Promise<void> {
    if (cloning) return;
    setError(null);
    setCloning(appId);
    try {
      const definition = await loadPublishedDefinition(appId);
      if (!definition) throw new Error('That app could not be loaded.');
      const result = buildRemix(definition, { appId, name, description });
      const project = await useProjectStore
        .getState()
        .createFromDefinition(result.name, result.description, result.definition);
      navigate(`/project/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That app could not be copied.');
    } finally {
      setCloning(null);
    }
  }

  return (
    <Screen title="Discover">
      <label className="type-caption field-label" htmlFor="search">Search apps</label>
      <input
        id="search"
        type="search"
        className="field"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="What are you looking for?"
      />

      {categories.length > 0 && (
        <div className="filter-row" role="group" aria-label="Categories">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={category === c}
              className={`chip-button ${category === c ? 'is-selected' : ''}`}
              onClick={() => setCategory(category === c ? null : c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {shown.length === 0 ? (
        <EmptyState icon="discover" title="Nothing matches" body="Try a different word, or clear the filters." />
      ) : (
        <ul className="project-list app-grid">
          {shown.map((a) => (
            <li key={a.appId} className="card clay-card discover-card">
              <div className="project-row">
                <IdentityChip name={a.name} />
                <span className="project-text">
                  <span className="type-heading">{a.name}</span>
                  <span className="type-caption project-meta">{a.description}</span>
                </span>
              </div>
              <div className="discover-actions">
                {a.siteUrl && (
                  <a className="btn btn-secondary clay-card pressable" href={a.siteUrl} target="_blank" rel="noreferrer">
                    Open
                  </a>
                )}
                {(a.remixOnly || canRemix(a as never)) && (
                  <Button
                    title={cloning === a.appId ? 'Copying…' : 'Remix'}
                    variant="ghost"
                    loading={cloning === a.appId}
                    onPress={() => void remix(a.appId, a.name, a.description)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}
